import {
	Component,
	EventEmitter,
	Input,
	Output,
	ViewEncapsulation
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatOptionModule } from '@angular/material/core';
import { NgFor, NgIf } from '@angular/common';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';

import { pageTransitions } from '../animations/page-transitions';
import { UserDatacenterView } from '../models/datacenter-aggrid-view';
import { NumberPipe } from '../pipes/number.pipe';
import { LoadingSpinnerComponent } from '../components/utils/loading-spinner.component';

function alphaSort(firstView: UserDatacenterView, secondView: UserDatacenterView) {
	return firstView.name.localeCompare(secondView.name, undefined, { sensitivity: 'base' });
}

@Component({
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	selector: 'copo-datacenter-tool-header',
	styleUrl: './datacenter-tool-header.component.css',
	templateUrl: './datacenter-tool-header.component.html',
	imports: [

		MatFormFieldModule,
		TranslateModule,

		MatSelectModule,
		NgFor,

		MatOptionModule,
		MatIconModule,
		NgIf,
		LoadingSpinnerComponent,
		NumberPipe,
		MatButtonModule
	]
})
export class DatacenterToolHeaderComponent {
	@Input() customizeViewDisabled = false;
	@Input() canResetFilters = false;
	@Input() currentView?: UserDatacenterView;
	@Input() download?: () => Promise<void>;
	@Input() nbResults?: number;
	@Input() nbSelectedResults?: number;
	@Input() views: UserDatacenterView[] = [];
	@Output() readonly customizeView = new EventEmitter<UserDatacenterView>(true);
	@Output() readonly resetFilters = new EventEmitter<void>(true);
	@Output() readonly viewChange = new EventEmitter<UserDatacenterView>(true);

	downloadInProgress = false;

	get sortedViews() {
		const favorites = this.views
			.filter(view => view.isFavorite)
			.sort(alphaSort);
		const defaults = this.views
			.filter(view => !view.isFavorite && view.isDefault)
			.sort(alphaSort);
		const others = this.views
			.filter(view => !view.isFavorite && !view.isDefault)
			.sort(alphaSort);
		return [...favorites, ...defaults, ...others];
	}

	viewComparator(ob1?: UserDatacenterView, ob2?: UserDatacenterView) {
		return ob1 && ob2 ? ob1.id === ob2.id : ob1 === ob2;
	}

	onChange(view: UserDatacenterView) {
		this.currentView = view;
		this.viewChange.emit(view);
	}

	onCustomize() {
		if (!this.views.some(view => view.id === this.currentView?.id)) {
			return;
		}
		this.customizeView.emit(this.currentView);
	}

	async onDownload() {
		if (this.download === undefined || !this.canDownload()) {
			return;
		}
		try {
			this.downloadInProgress = true;
			await this.download();
		} catch (error) {
			console.error('Error when downloading datacenter data', error);
		} finally {
			this.downloadInProgress = false;
		}
	}

	canDownload() {
		return !this.downloadInProgress && this.download !== undefined && this.nbResults && this.nbResults > 0;
	}
}
